import { useEffect, useState } from "react";

import { isPublicRoutePath } from "@/lib/auth/publicRoutePaths";

type VendorChunk = { load: () => Promise<void>; loaded: () => boolean; };

let chunk: VendorChunk | null = null;

/**
 * Called by the web route context (`webRouteContext.ts`), which owns the `(app)` chunk. The
 * dependency points this way so a component can preload the vendor app without importing Expo
 * Router's route context. Unregistered, as in Storybook, there is nothing to wait for.
 */
export function registerVendorChunk(next: VendorChunk): void {
  chunk = next;
}

/**
 * Whether the root layout may render on web (TRO-1972). A cold open of a vendor page waits for the
 * `(app)` chunk, as it waits for fonts, so `(app)/_layout` mounts synchronously the way it does on
 * native. Rendered while still loading, `(app)` suspends, the router's path collapses to
 * `/activity`, and the auth gate records that as where a signed-out visitor was headed.
 */
export function useVendorChunkReady(): boolean {
  const [ready, setReady] = useState(
    () => !chunk || chunk.loaded() || isPublicRoutePath(globalThis.location?.pathname ?? ""),
  );
  useEffect(() => {
    if (!ready) void preloadVendorApp().then(() => setReady(true));
  }, [ready]);
  return ready;
}

/**
 * Loads the `(app)` chunk before a public page sends someone into the vendor app. Pushed while it
 * is still loading, `(app)` suspends and the auth gate records `/activity` instead of where the
 * visitor was headed, so await this before `router.push` to an `(app)` route.
 */
export function preloadVendorApp(): Promise<void> {
  return chunk?.load() ?? Promise.resolve();
}
